import PropTypes from "prop-types";
import Immutable from "immutable";
import Globalize from "globalize";

const DEFAULT_RETRY_TIME = 5000;

export default function ({
    watchViewAs = false,
    pageSize = 50,
    watchFilters = true,
    watchSearch = true,
    watchSort = true,
    watchSearchField = true,
} = {}) {
    return (Component) => {
        class EnhancedComponent extends Component {
            constructor(props, context) {
                super(props, context);
                this.state = this.state || {};
                this.state.failedTimeout = DEFAULT_RETRY_TIME;

                if (!this.collection) {
                    throw new Error("Please set a collection");
                }
            }

            UNSAFE_componentWillMount() {
                if (super.UNSAFE_componentWillMount) {
                    super.UNSAFE_componentWillMount();
                }

                this.collection.on("sync", this.handleFetchSuccess, this);
                this.collection.on("error", this.handleFetchError, this);
                this.collection.on("abort", this.handleFetchAbort, this);
            }

            componentWillUnmount() {
                clearTimeout(this.retryTimeout);
                if (super.componentWillUnmount) {
                    super.componentWillUnmount();
                }

                this.collection.off("sync", this.handleFetchSuccess, this);
                this.collection.off("error", this.handleFetchError, this);
                this.collection.off("abort", this.handleFetchAbort, this);
            }

            UNSAFE_componentWillReceiveProps(nextProps, nextContext) {
                if (
                    (watchSearch && nextProps.searchTerm !== this.props.searchTerm) ||
                    (watchSearch && nextProps.bulkSearch !== this.props.bulkSearch) ||
                    (watchSearch && nextProps.bulkSearch && nextProps.bulkSearchType !== this.props.bulkSearchType) ||
                    (watchSort && nextProps.sortOrder !== this.props.sortOrder) ||
                    (watchSort && nextProps.sort !== this.props.sort) ||
                    (watchSort && nextContext.sort !== this.context.sort) ||
                    (watchSort && nextContext.sortOrder !== this.context.sortOrder) ||
                    (watchFilters && nextProps.filters !== this.props.filters) ||
                    (watchFilters && nextContext.filters !== this.context.filters) ||
                    (watchSearchField && nextProps.searchField !== this.props.searchField) ||
                    (watchSearchField && nextContext.searchField !== this.context.searchField) ||
                    (watchViewAs &&
                        (nextContext.viewAs !== this.context.viewAs || nextProps.viewAs !== this.props.viewAs))
                ) {
                    this.fetchNextPage(true, nextProps, nextContext);
                }

                if (super.UNSAFE_componentWillReceiveProps) {
                    super.UNSAFE_componentWillReceiveProps(nextProps, nextContext);
                }
            }

            handleFetchSuccess(collection, data, options) {
                if (Array.isArray(data)) {
                    if (super.handleFetchSuccess) {
                        super.handleFetchSuccess(collection, data, options);
                    }

                    this.setState({
                        failedTimeout: DEFAULT_RETRY_TIME,
                    });
                }
            }

            handleFetchError(collection, response, options) {
                if (super.handleFetchError) {
                    if (super.handleFetchError(collection, response, options) === false) {
                        return false;
                    }
                }

                const { failedTimeout } = this.state;
                const statusCode = response.rawRequest && response.rawRequest.status ? response.rawRequest.status : 500;

                if (statusCode >= 300 && statusCode < 500) {
                    this.setState({
                        failedTimeout: DEFAULT_RETRY_TIME,
                    });
                } else {
                    if (failedTimeout !== DEFAULT_RETRY_TIME && this.props.onSnackbarAdd) {
                        // too many errors, alert user
                        this.props.onSnackbarAdd({
                            message: Globalize.formatMessage("FETCH_ERROR", {
                                retryTime: Math.floor(failedTimeout / 1000),
                            }),
                        });
                    }
                    this.retryTimeout = setTimeout(() => {
                        this.fetchNextPage();
                    }, failedTimeout);

                    this.setState({
                        failedTimeout: failedTimeout * 2,
                    });
                }
                return true;
            }

            handleFetchAbort() {
                if (super.handleFetchAbort) {
                    super.handleFetchAbort();
                }

                this.setState({ failedTimeout: DEFAULT_RETRY_TIME });
            }

            fetchNextPage(reset, props = this.props, context = this.context, state = this.state) {
                let { viewAs } = context;

                if (!viewAs) {
                    viewAs = props.viewAs;
                }

                const filters = watchFilters && (context.filters || props.filters);
                const searchTerm = watchSearch && (context.searchTerm || props.searchTerm);
                const searchField = watchSearchField && (context.searchField || props.searchField);

                const fields = props.fields || "__all";

                const sort = watchSort && (context.sort || props.sort);
                const sortOrder = watchSort && (context.sortOrder || props.sortOrder);

                if (this.onFetchNextPage) {
                    super.onFetchNextPage(reset, props, context);
                }

                clearTimeout(this.retryTimeout);

                const options = {};
                if (reset) {
                    options.reset = true;
                    this.setState({ failedTimeout: DEFAULT_RETRY_TIME });
                } else {
                    options.reset = false;
                    options.remove = false;
                    options.merge = true;
                }

                let data = (filters ? filters : Immutable.Map())
                    .set("__fields", fields)
                    .set("__limit", props.pageSize || pageSize);

                if (searchTerm) {
                    data = data.set("__search", searchTerm);
                }

                if (searchField) {
                    data = data.set("__search_field", searchField);
                }

                const bulkSearch = watchSearch && (context.bulkSearch || props.bulkSearch);
                if (bulkSearch) {
                    data = data.set("__bulk_search", bulkSearch);
                    if (props.bulkSearchType) {
                        data = data.set("__bulk_search_type", props.bulkSearchType);
                    }
                }

                if (sort) {
                    data = data.set("__sort", sort);
                }

                if (sortOrder) {
                    data = data.set("__sort_order", sortOrder);
                }

                if (watchViewAs && viewAs) {
                    data = data.set("__company", viewAs._id);
                }

                if (this.enhanceQueryParams) {
                    data = this.enhanceQueryParams(data, props, context, state);
                }

                options.data = data.toJS();

                //Prevent calls that we know will never yield results
                if (!this.state.hasNoResults) {
                    return this.collection.fetch(options);
                } else {
                    this.setState({ loading: false, hasMoreElements: false });
                    return undefined;
                }
            }
        }

        EnhancedComponent.contextTypes = {
            filters: PropTypes.object,
            sort: PropTypes.string,
            sortOrder: PropTypes.oneOf(["asc", "desc"]),
            viewAs: PropTypes.object,
            ...EnhancedComponent.contextTypes,
        };

        return EnhancedComponent;
    };
}
